/**
 * HouseholdBreadcrumb — page-level breadcrumb with built-in household switcher.
 *
 * Renders: Husstander › [Current household ▾].
 * The household segment is a dropdown that lists every household the user
 * belongs to, plus a link back to the full households list.
 */

import { useNavigate } from "@tanstack/react-router";

import { t } from "../i18n";
import { trpc } from "../lib/trpc";
import { Breadcrumb, type BreadcrumbItem, MenuItem, MenuLabel, MenuSeparator } from "./ui";

export interface HouseholdBreadcrumbProps {
  currentHouseholdId: string;
  currentHouseholdName: string;
}

export function HouseholdBreadcrumb({
  currentHouseholdId,
  currentHouseholdName,
}: HouseholdBreadcrumbProps) {
  const navigate = useNavigate();
  const households = trpc.household.list.useQuery();

  const goToHouseholds = () => navigate({ to: "/" });
  const goToHousehold = (id: string) =>
    navigate({
      to: "/households/$householdId/$tab",
      params: { householdId: id, tab: "settlements" },
    });

  const items: BreadcrumbItem[] = [
    {
      label: t.households.title,
      icon: "home",
      onClick: goToHouseholds,
    },
    {
      label: currentHouseholdName,
      dropdown: (
        <div className="min-w-[14rem] max-w-[calc(100vw-2rem)]">
          <MenuLabel>{t.appShell.myHouseholdsLabel}</MenuLabel>
          {households.data?.map((h) => (
            <MenuItem
              key={h.id}
              icon={h.id === currentHouseholdId ? "check" : undefined}
              onClick={() => goToHousehold(h.id)}
            >
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate">{h.name}</span>
                <span className="truncate text-ink-500 text-xs">
                  {t.appShell.memberCount(h.memberCount)} ·{" "}
                  {h.role === "owner" ? t.roles.owner : t.roles.member}
                </span>
              </div>
            </MenuItem>
          ))}
          <MenuSeparator />
          <MenuItem icon="users" onClick={goToHouseholds}>
            {t.appShell.allHouseholds}
          </MenuItem>
        </div>
      ),
    },
  ];

  return <Breadcrumb items={items} />;
}
